"use client";

import { useTranslations } from "next-intl";
import { Image as DatoImage } from "react-datocms/image";

import { TripadvisorRating } from "~/components/tripadvisor-rating";
import { type FragmentOf } from "~/lib/dato-graphql";
import type {
  responsiveImageFragment,
  tourPropsFragment,
} from "~/lib/fragments";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { Link } from "~/navigation";
import { type RouterOutputs } from "~/trpc/types";

export type Tour = FragmentOf<typeof tourPropsFragment> & {
  heroImage?: {
    responsiveImage?: FragmentOf<typeof responsiveImageFragment> | null;
  } | null;
};

export function TourCard({
  tour,
  inventory,
}: {
  inventory?: Pick<
    RouterOutputs["inventory"]["search"]["results"][0],
    "amount" | "perPerson" | "locationName"
  >;
  tour: Tour;
}) {
  const t = useTranslations("TourDetails");
  const amount = inventory?.amount ?? tour.fromPrice;
  const perPerson = inventory?.perPerson ?? tour.perPerson;
  const formatCurrency = useCurrencyFormatter();
  return (
    <Link href={`/tours/${tour.slug}/${tour.id}`}>
      {tour.heroImage?.responsiveImage ? (
        <DatoImage
          className="mb-4 rounded-xl"
          data={tour.heroImage.responsiveImage}
        />
      ) : null}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="text-muted flex items-center gap-1 text-xs font-light leading-none">
            {tour.tripadvisorRating ? (
              <TripadvisorRating size="small" value={tour.tripadvisorRating} />
            ) : null}
            {tour.tripadvisorRating && tour.duration ? (
              <div className="bg-muted mx-1 size-1 rounded-full" />
            ) : null}
            {tour.duration ? <div>{tour.duration}</div> : null}
          </div>
          {inventory ? (
            <div className="text-muted text-sm font-light leading-tight">
              {inventory.locationName}
            </div>
          ) : null}
        </div>
        <div className="mt-1 font-medium leading-tight">{tour.title}</div>
        <div className="text-muted flex items-baseline gap-1 text-sm font-light leading-tight">
          <span className="text-base text-neutral-900">
            {t("fromAmount", {
              currencyAmount: formatCurrency(amount),
            })}
          </span>{" "}
          {perPerson ? t("perPerson") : t("perGroup")}
        </div>
      </div>
    </Link>
  );
}
